/**
 * Reading the `range` hint a provider may publish on a `number` field.
 *
 * Two consumers, hence its own module: the value editor (which renders the bound and refuses a
 * value outside it) and `validateConditionsTree` (which blocks the save on a stored leaf that is
 * already outside it). Both must agree on what "declared" and "inside" mean.
 *
 * `validateConditionsTree` runs on the notification and assignment dialogs only — its two callers.
 * The guardrails dialog does not validate its tree, and `ai.confidence_score` is the only bounded
 * field today, so there the refused value reaches the backend as `''` and `coerce_scalar` rejects
 * it (`rules_engine/internal/service.py` turns that into a 400).
 */

import type { NumericRangeHints } from './types';
import { RANGE_HINT_KEY } from './types';

/** The declared bound of a numeric field, or `undefined` when it declares none. A block missing
 *  either end reads as no bound rather than half of one — the hints dict is open-ended, so a
 *  partial block is a producer we do not understand, not a constraint to guess at. */
export const getNumericRange = (hints?: Record<string, unknown> | null): NumericRangeHints | undefined => {
  const range = hints?.[RANGE_HINT_KEY] as Partial<NumericRangeHints> | undefined;
  return typeof range?.min === 'number' && typeof range?.max === 'number'
    ? { min: range.min, max: range.max }
    : undefined;
};

/** Whether `value` sits inside the bound, both ends inclusive.
 *
 *  An empty or non-numeric value is *not* reported out of range: "needs a value" and "must be a
 *  number" are separate errors the callers already raise, and answering with a range message here
 *  would name the wrong remedy. */
export const isWithinNumericRange = (value: unknown, range: NumericRangeHints): boolean => {
  if (value === '' || value === null || value === undefined) {
    return true;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) || (parsed >= range.min && parsed <= range.max);
};

/** Inline copy naming the bound, e.g. `Must be between 0 and 1.` */
export const numericRangeMessage = (range: NumericRangeHints): string =>
  `Must be between ${range.min} and ${range.max}.`;
